/**
 * Extract product alias from is_locked_url (e.g. https://spiking.com/purchase/pro)
 * Maps product IDs to /upgrade-checkout/:productAlias
 * @param {string} url - is_locked_url from API
 * @returns {string} product alias for upgrade-checkout route
 */
export function getProductAliasFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const match = url.match(/\/purchase\/([^/?]+)/);
    const slug = match ? match[1].toLowerCase() : null;
    if (!slug) return null;

    // Map product IDs (from URL) to upgrade-checkout product aliases
    const slugToAlias = {
      fiat: "analysis-fiat",
      spikingfiat: "analysis-fiat",
      insider: "insider-investors",
      spikinginsider: "insider-investors",
      active: "active-traders",
      spikingactive: "active-traders",
      top10buy: "top10-buysell",
      top10buysell: "top10-buysell",
      top30ai: "top30-ai-stocks",
      top30aistocks: "top30-ai-stocks",
      top200sma: "top200-sma",
      spikingtop200sma: "top200-sma",
      ai: "ai",
      spikingai: "ai",
      portfolios: "holy-grail",
      spikingportfolios: "holy-grail",
      trillion: "trillion",
      dailybread: "daily-bread",
      spikingdailybread: "daily-bread",
      resurrection: "resurrection",
      triangulation: "triangulation",
      tttn: "tttn",
      tttn4: "tttn",
      coaching: "mars",
      coaching12: "mars",
      cosy: "cosy",
      cosy20: "cosy",
      courses: "premium-courses",
      spikingcourses: "premium-courses",
      centurion: "centurion",
      spikingcenturion: "centurion",
    };
    return slugToAlias[slug] || null;
  } catch {
    return null;
  }
}

export const UPGRADE_CHECKOUT_BASE = "https://spiking.com/upgrade-checkout";

/**
 * Get upgrade checkout URL from url or productAlias
 * @param {string} urlOrAlias - is_locked_url or product alias
 * @param {boolean} fromUrl - if true, treat as URL and extract alias
 * @param {string} defaultAlias - when url is empty or doesn't match /purchase/, use this (e.g. "insider-investors")
 * @returns {string} external URL like https://spiking.com/upgrade-checkout/pro, or /pricing-premium fallback
 */
export function getUpgradeCheckoutPath(
  urlOrAlias,
  fromUrl = true,
  defaultAlias = null
) {
  if (fromUrl) {
    if (!urlOrAlias || !/\/purchase\/[^/?]+/.test(urlOrAlias)) {
      return defaultAlias ? `${UPGRADE_CHECKOUT_BASE}/${defaultAlias}` : "/pricing-premium";
    }
    const alias = getProductAliasFromUrl(urlOrAlias);
    return alias ? `${UPGRADE_CHECKOUT_BASE}/${alias}` : "/pricing-premium";
  }
  const resolved = urlOrAlias || defaultAlias;
  return resolved ? `${UPGRADE_CHECKOUT_BASE}/${resolved}` : "/pricing-premium";
}

/**
 * Navigate to upgrade checkout or /pricing-premium fallback.
 * Uses window.location.href for external upgrade-checkout URLs,
 * history.push for internal /pricing-premium fallback.
 */
export function navigateToUpgradeCheckout(history, urlOrAlias, fromUrl = true, defaultAlias = null) {
  const target = getUpgradeCheckoutPath(urlOrAlias, fromUrl, defaultAlias);
  if (target.startsWith("http")) {
    window.location.href = target;
  } else {
    history.push(target);
  }
}
